/**
 * Le FORME delle forniture (capitolo 166): fornitori sulla piattaforma, listini, condizioni
 * per cliente, richieste d'offerta sigillate, ordini ricevuti, accordi con la piattaforma e
 * provvigioni. Condivise fra gestionale, console e Cloud Functions (via sync). Nessun Firebase.
 *
 * Gli id degli enum sono ASCII e non si traducono mai (regola cardine #1).
 */

/** Il profilo del fornitore: i dati DAC7 che la piattaforma deve raccogliere e comunicare. */
export interface ProfiloFornitore {
  /** = id del tenant fornitore. */
  id: string;
  denominazione: string;
  partita_iva: string;
  codice_fiscale?: string | null;
  sede: { via: string; cap: string; citta: string; provincia: string; nazione: string };
  iban?: string | null;
  identificativo_fiscale?: string | null;
  email_ordini: string;
  regioni_servite: string[];
  minimo_ordine_eur?: number | null;
  /** Regole di trasporto stampate sull'offerta: testo libero del fornitore. */
  trasporto?: string | null;
  /** Vero quando i campi DAC7 obbligatori sono compilati: senza, le offerte non si accendono. */
  pronto_dac7: boolean;
}

export const DISPONIBILITA_OFFERTA = ['a_magazzino', 'su_ordine', 'esaurito'] as const;
export type DisponibilitaOfferta = (typeof DISPONIBILITA_OFFERTA)[number];

export interface OffertaFornitore {
  /** `${fornitore_tenant_id}__${componente_ref}` */
  id: string;
  fornitore_tenant_id: string;
  componente_ref: string;
  codice_articolo_fornitore?: string | null;
  prezzo_listino: number;
  valuta: 'EUR';
  disponibilita: DisponibilitaOfferta;
  quantita_disponibile?: number | null;
  lead_time_giorni: number;
  /** Minimo d'ordine, in pezzi. */
  moq: number;
  confezione?: number | null;
  promozione?: { prezzo: number; scade_il: string } | null;
  raee_per_pezzo?: number | null;
  attiva: boolean;
}

// Le condizioni riservate a un cliente (`forniture_condizioni`) sono dal 28/10/2026 l'ACCORDO fra fornitore e cliente
// (atlante v2, cap. 428, tappa 1-bis): la forma vive nel motore, `src/modules/catalog-v2/engine/accordoFornituraV2.ts`
// (`AccordoDiFornituraV2`), perché decide i prezzi dei preventivi e server e browser devono leggerla uguale.

// `scaduta`: una richiesta di merci (`modo: 'convenzione'`) a cui il fornitore non ha risposto entro la scadenza. Una gara
// alla scadenza è `chiusa` (le offerte sigillate si aprono); una richiesta di merci senza risposta non ha niente da aprire.
export const STATI_RICHIESTA = ['aperta', 'chiusa', 'aggiudicata', 'annullata', 'scaduta'] as const;
export type StatoRichiesta = (typeof STATI_RICHIESTA)[number];

export interface RigaRichiesta {
  /** Il componente del magazzino (gara del cap. 166). Nelle merci del cantiere (cap. 438) è la chiave della riga, `riga_id`. */
  componente_ref: string;
  descrizione: string;
  quantita: number;
  /** Merci del cantiere (cap. 438): la chiave stabile della riga, su cui il fornitore risponde. */
  riga_id?: string;
  /** Merci del cantiere: il prodotto del catalogo del fornitore (`catalog_v2_products`), quando la riga lo ha trovato. */
  prodotto_ref?: string | null;
  codice?: string | null;
  /** Unità di misura (pezzi, metri…), dato in chiaro. */
  unita?: string | null;
  /** Merci del cantiere: il prezzo d'acquisto atteso per l'azienda (listino in vigore − sconto dell'accordo), dal server. */
  prezzo_previsto?: number | null;
  /** Merci del cantiere: la riga non è nel catalogo del fornitore, il prezzo lo mette lui. */
  fuori_catalogo?: boolean;
}

/** Come nasce una richiesta: gara sigillata fra più fornitori (cap. 166) o al fornitore dell'accordo (cap. 438). */
export const MODI_RICHIESTA = ['gara', 'convenzione'] as const;
export type ModoRichiesta = (typeof MODI_RICHIESTA)[number];

/** Da dove nasce una richiesta di merci del cantiere (cap. 438). */
export const ORIGINI_MERCI = ['richiesta_installazione', 'lavoro_ricevuto'] as const;
export type OrigineMerci = (typeof ORIGINI_MERCI)[number];

export interface RichiestaOfferta {
  id: string;
  richiedente_tenant_id: string;
  righe: RigaRichiesta[];
  regione: string;
  /** ISO. Prima di questa data le offerte restano sigillate. */
  scade_il: string;
  fornitori_invitati: string[];
  stato: StatoRichiesta;
  /** Anonima finché non aggiudicata (tranne per chi ha il richiedente come cliente storico). */
  anonima: boolean;
  ordine_ref?: string | null;
  aggiudicata_a?: string | null;
  note?: string | null;
  creata_at: string;
  /** Assente = 'gara' (le richieste del cap. 166). */
  modo?: ModoRichiesta;
  /** Merci del cantiere: la persona del fornitore che segue l'azienda (dall'accordo), o null. */
  referente_uid?: string | null;
  /** Merci del cantiere: la richiesta di installazione o il lavoro da cui nasce. */
  origine?: { tipo: OrigineMerci; id: string } | null;
}

export interface RigaOffertaRicevuta {
  /** Il componente della riga (gara) o la sua chiave `riga_id` (merci del cantiere). */
  componente_ref: string;
  prezzo_unitario: number;
  disponibile: boolean;
  lead_time_giorni: number;
  quantita_offerta: number;
}

export interface OffertaRicevuta {
  /** `${richiesta_id}__${fornitore_tenant_id}` */
  id: string;
  richiesta_id: string;
  fornitore_tenant_id: string;
  fornitore_nome: string;
  righe: RigaOffertaRicevuta[];
  trasporto_eur: number;
  validita_giorni: number;
  note?: string | null;
  inviata_at: string;
  /** Somma delle righe disponibili × quantità + trasporto: per confrontare. */
  totale_eur: number;
  copertura: number;
}

export const STATI_ORDINE_RICEVUTO = ['nuovo', 'confermato', 'spedito', 'evaso_parziale', 'evaso', 'rifiutato'] as const;
export type StatoOrdineRicevuto = (typeof STATI_ORDINE_RICEVUTO)[number];

/** Lo specchio dell'ordine per il fornitore: stesso id dell'ordine del magazzino. */
export interface OrdineRicevuto {
  id: string;
  fornitore_tenant_id: string;
  cliente_tenant_id: string;
  cliente_nome: string;
  numero: string;
  /**
   * `quantita_evasa`: quanti pezzi il fornitore ha già fatto uscire dal SUO magazzino per
   * questa riga (documenti di vendita confermati con `ordine_cliente_ref`). Assente = 0.
   * Per un fornitore senza magazzino resta 0 e lo stato si dichiara a mano.
   */
  righe: Array<{ componente_ref: string; descrizione: string; quantita: number; prezzo_unitario: number; quantita_evasa?: number }>;
  imponibile_eur: number;
  trasporto_eur: number;
  stato: StatoOrdineRicevuto;
  ddt_fornitore?: { numero: string; data: string } | null;
  note?: string | null;
  richiesta_ref?: string | null;
  /** Da quale richiesta nasce: gara del cap. 166 o merci del cantiere (cap. 438). Assente sugli ordini vecchi = gara. */
  modo?: ModoRichiesta;
  cliente_storico: boolean;
  creato_at: string;
  confermato_at?: string | null;
}

export const MODELLI_ACCORDO = ['canone', 'provvigione', 'misto'] as const;
export type ModelloAccordo = (typeof MODELLI_ACCORDO)[number];

export interface VersioneAccordo {
  modello: ModelloAccordo;
  canone_mensile_eur: number;
  provvigione_percentuale: number;
  esenzione_clienti_storici: boolean;
  minimo_mensile_eur?: number | null;
  /** ISO date di decorrenza: il conto del mese usa la versione in vigore. */
  valido_da: string;
  stripe_price_id?: string | null;
  note?: string | null;
}

/** L'accordo del fornitore con la piattaforma: scrive solo il super utente (console). */
export interface AccordoPiattaforma extends VersioneAccordo {
  /** = id del tenant fornitore. */
  id: string;
  storico: VersioneAccordo[];
}

export interface RigaProvvigione {
  ordine_ref: string;
  numero: string;
  cliente_tenant_id: string;
  cliente_nome: string;
  imponibile_eur: number;
  /** L'esenzione dei clienti storici vale solo nelle gare: sulle merci del cantiere la provvigione c'è sempre. */
  modo: ModoRichiesta;
  cliente_storico: boolean;
  esente: boolean;
  provvigione_eur: number;
}

export interface ProvvigioniMese {
  /** `${fornitore_tenant_id}__${YYYY-MM}` */
  id: string;
  fornitore_tenant_id: string;
  mese: string;
  accordo: VersioneAccordo | null;
  righe: RigaProvvigione[];
  imponibile_totale_eur: number;
  provvigioni_eur: number;
  canone_eur: number;
  minimo_applicato_eur: number;
  totale_dovuto_eur: number;
  calcolato_at: string;
}

export interface ClienteProtetto {
  /** `${fornitore_tenant_id}__${cliente_tenant_id}` */
  id: string;
  fornitore_tenant_id: string;
  cliente_tenant_id: string;
  registrato_at: string;
  /** ISO: dopo questa data la protezione scade (180 giorni), salvo nuovi ordini. */
  scade_il: string;
}
